import { IMPORT_FILE_EXTENSIONS } from '../../lib/stockImportsFileFormats'
import type {
  ImportFileFormat,
  StockImportConfigRequest,
} from '../../types/stockImports.types'
import {
  type ConfigFormInput,
  type ConfigFormValues,
  normalizeCronExpression,
} from './configFormModel'

const toApiIndex = (number: number) => number - 1

const toOptionalApiIndex = (number?: number) =>
  number === undefined ? null : toApiIndex(number)

const trimOptionalString = (value?: string) => value?.trim() || undefined

const splitList = (value?: string) =>
  value
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const parseIndexes = (value?: string) =>
  splitList(value)
    ?.map(Number)
    .filter((item) => Number.isInteger(item) && item >= 0)

const FILE_FORMAT_EXTENSIONS: Record<ImportFileFormat, string> = {
  EXCEL: 'xlsx',
  CSV: 'csv',
  XML: 'xml',
}

export const getFileMaskPlaceholder = (fileFormat: ImportFileFormat) =>
  `Остатки_*.${FILE_FORMAT_EXTENSIONS[fileFormat]}`

const FILE_MASK_EXTENSION_PATTERN = new RegExp(
  `\\.(${Object.values(IMPORT_FILE_EXTENSIONS).flat().join('|')})$`,
  'i'
)

export const normalizeFileMaskByFormat = (
  value: string | undefined,
  fileFormat: ImportFileFormat
) => {
  const trimmedValue = trimOptionalString(value)
  if (!trimmedValue) return undefined

  const existingExtension = trimmedValue
    .match(FILE_MASK_EXTENSION_PATTERN)?.[1]
    ?.toLowerCase()
  if (
    existingExtension &&
    IMPORT_FILE_EXTENSIONS[fileFormat].includes(existingExtension)
  ) {
    return trimmedValue
  }

  return `${trimmedValue.replace(FILE_MASK_EXTENSION_PATTERN, '')}.${FILE_FORMAT_EXTENSIONS[fileFormat]}`
}

type SourceKeyValues = Pick<
  ConfigFormInput,
  | 'sourceType'
  | 'url'
  | 'imapHost'
  | 'imapPort'
  | 'imapLogin'
  | 'imapPasswordEncrypted'
  | 'useSsl'
  | 'expectedSender'
  | 'subjectMask'
  | 'fileMask'
>

export const buildSourceValidationKey = (values: SourceKeyValues) => {
  if (values.sourceType === 'URL') {
    const url = trimOptionalString(values.url)
    return url ? JSON.stringify({ sourceType: values.sourceType, url }) : null
  }

  if (values.sourceType === 'CUSTOM_IMAP') {
    const imapLogin = trimOptionalString(values.imapLogin)
    const imapPasswordEncrypted = trimOptionalString(
      values.imapPasswordEncrypted
    )
    if (!imapLogin || !imapPasswordEncrypted) return null

    return JSON.stringify({
      sourceType: values.sourceType,
      imapHost: trimOptionalString(values.imapHost) ?? null,
      imapPort: trimOptionalString(values.imapPort) ?? null,
      imapLogin,
      imapPasswordEncrypted,
      useSsl: values.useSsl,
    })
  }

  if (values.sourceType === 'EMAIL') {
    const expectedSender = trimOptionalString(values.expectedSender)
    const subjectMask = trimOptionalString(values.subjectMask)
    if (!expectedSender || !subjectMask) return null

    return JSON.stringify({
      sourceType: values.sourceType,
      expectedSender,
      subjectMask,
      fileMask: trimOptionalString(values.fileMask) ?? null,
    })
  }

  return null
}

const buildSourceSettings = (
  values: ConfigFormValues
): StockImportConfigRequest['sourceSettings'] => {
  const sourceSettings: StockImportConfigRequest['sourceSettings'] = {
    startRowIndex:
      values.startRowIndex === undefined
        ? undefined
        : toApiIndex(values.startRowIndex),
    endRowIndex:
      values.endRowIndex === undefined
        ? undefined
        : toApiIndex(values.endRowIndex),
  }

  if (values.fileFormat === 'EXCEL') {
    sourceSettings.sheetSelectionRule = {
      selectionType: values.sheetSelectionType,
      ...(values.sheetSelectionType === 'BY_NAME'
        ? { sheetNames: splitList(values.sheetNames) }
        : {}),
      ...(values.sheetSelectionType === 'BY_INDEX'
        ? { sheetIndexes: parseIndexes(values.sheetIndexes) }
        : {}),
    }
  }

  if (values.sourceType === 'URL') {
    sourceSettings.cloudUrlSettings = { url: trimOptionalString(values.url) }
  }

  if (values.sourceType === 'EMAIL') {
    sourceSettings.fileMask = normalizeFileMaskByFormat(
      values.fileMask,
      values.fileFormat
    )
    sourceSettings.emailSettings = {
      expectedSender: trimOptionalString(values.expectedSender),
      subjectMask: trimOptionalString(values.subjectMask),
    }
  }

  if (values.sourceType === 'CUSTOM_IMAP') {
    sourceSettings.fileMask = normalizeFileMaskByFormat(
      values.fileMask,
      values.fileFormat
    )
    sourceSettings.customImapSettings = {
      imapHost: trimOptionalString(values.imapHost),
      imapPort: trimOptionalString(values.imapPort),
      imapLogin: trimOptionalString(values.imapLogin),
      imapPasswordEncrypted: trimOptionalString(values.imapPasswordEncrypted),
      useSsl: values.useSsl,
      expectedSender: trimOptionalString(values.expectedSender),
      subjectMask: trimOptionalString(values.subjectMask),
    }
  }

  if (values.sourceType === 'MANUAL') {
    sourceSettings.fileMask = trimOptionalString(values.fileMask)
  }

  return sourceSettings
}

export const buildConfigRequest = (
  supplierId: string | null,
  values: ConfigFormValues,
  status: StockImportConfigRequest['status']
): StockImportConfigRequest => {
  const textQuantityMapping = Object.fromEntries(
    (values.textQuantityMapping ?? []).map((row) => [row.key, row.value])
  )

  return {
    productSupplierId: supplierId,
    storeId: values.storeId,
    status,
    sourceType: values.sourceType,
    fileFormat: values.fileFormat,
    cronExpression:
      values.sourceType === 'EMAIL'
        ? null
        : normalizeCronExpression(values.cronExpression),
    expirationHours: values.expirationHours,
    excludeWeekends: values.excludeWeekends,
    sourceSettings: buildSourceSettings(values),
    columnMapping: {
      articleColumnNumber: toApiIndex(values.articleColumnNumber),
      articleInchColumnNumber: toOptionalApiIndex(
        values.articleInchColumnNumber
      ),
      barcodeColumnNumber: toOptionalApiIndex(values.barcodeColumnNumber),
      descriptionColumnNumber: toApiIndex(values.descriptionColumnNumber),
      brandColumnNumber: toOptionalApiIndex(values.brandColumnNumber),
      quantityColumnNumber: toApiIndex(values.quantityColumnNumber),
    },
    filters: {
      allowedBrands: values.allowedBrands?.length
        ? values.allowedBrands
        : undefined,
      ignoredBrands: values.ignoredBrands?.length
        ? values.ignoredBrands
        : undefined,
    },
    stockRules: {
      fallbackBrandId: values.fallbackBrandId,
      detectBrandInDescription: values.detectBrandInDescription,
      maxAllowedMissingPercentage: values.maxAllowedMissingPercentage,
      missingPercentageSpikeAlert: values.missingPercentageSpikeAlert,
      minLoadedPercentOfMedian: values.minLoadedPercentOfMedian,
      maxInvalidQuantityPercentage: values.maxInvalidQuantityPercentage,
      stalledAlertBusinessDays: values.stalledAlertBusinessDays,
      expectedQuantityHeader: values.expectedQuantityHeader,
      textQuantityMapping:
        Object.keys(textQuantityMapping).length > 0
          ? textQuantityMapping
          : undefined,
    },
  }
}
